import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { getGameGroup, getPlayGameUrl, getBBAGPlayGameUrl, getGameListByListId, getTryGameTempUrl, getTryGameUrl } from '@api/game';
import { IMAGE_DOMAINS, STATUS_CODE } from '@constants/api';
import { useTranslation } from 'react-i18next';
import { LOCALS, LOCALS_IGS_CODE_MAP, IS_AGT, IS_NEW_AW, IS_SEAT_ONLY } from '@constants/config';
import { SEAT_GROUP_BY_GRP, GRP_BY_SEAT_GROUP, getSeatList, findSeatGame, decodeSeatCacc } from '@constants/seatGroups';
import CasinoGameCard from '@components/casino/CasinoGameCard';
import { MAX_MOBILE_WIDTH, MAX_PAD_WIDTH } from '@constants/numbers';
import NewsModal from './NewsModal';
import { getNews, getGameVideos, getEducationalTraining, getCommissionCalculation } from '@api/other';
import { useGlobalProps } from '@hooks/useGlobalProps';
import { getDVCInLS, getJKeyInLS } from '@utils/ls';
import { useNavigate, useLocation } from 'react-router-dom';
import { SWITCH_ROUTES } from '@constants/routes';
import useMobileDetect from '@hooks/useMobileDetect';
import ConfirmModal from '@components/shared/modals/ConfirmModal';
import LoginModal from '@components/shared/modals/LoginModal';
import CasinoSeatModal from '@components/casino/CasinoSeatModal';

const NEWS_TAB_GRP = 'NEWS';
const GAME_VIDEOS_TAB_GRP = 'GAME_VIDEO';
const EDUCATIONAL_TRAINING_TAB_GRP = 'EDUCATIONAL_TRAIN';
const COMMISSION_CALCULATION_TAB_GRP = 'COMMISSION';
const CASINO_TAB_GRP = 'CASINO';

export default function TabsComponent() {
    const [activeTab, setActiveTab] = useState(0);
    const [gameGroups, setGameGroups] = useState([]);
    const [news, setNews] = useState(null);
    const [gameVideos, setGameVideos] = useState(null);
    const [educationalTraining, setEducationalTraining] = useState(null);
    const [commissionCalculation, setCommissionCalculation] = useState(null);
    const [games, setGames] = useState([]);
    const [selectedNews, setSelectedNews] = useState(null);
    const [selectedGameVideo, setSelectedGameVideo] = useState(null);
    const [selectedEducationalTraining, setSelectedEducationalTraining] = useState(null);
    const [selectedCommissionCalculation, setSelectedCommissionCalculation] = useState(null);
    const [selectedCasinoGame, setSelectedCasinoGame] = useState(null);
    const { i18n, t } = useTranslation();
    const { user, homeActiveTab, updateHomeActiveTab, tryUKey, reGetuserInfo } = useGlobalProps();

    // 入座制：JP 清單來自 getUserInfo；legacy 站台沿用 casino_count/casino_list
    const jpList = IS_SEAT_ONLY ? getSeatList(user, 'JP') : null;
    const hasCasino = IS_SEAT_ONLY
        ? !!jpList
        : typeof user?.casino_count === 'number' && user.casino_count > 0;
    const casinoList = IS_SEAT_ONLY ? (jpList || []) : (user?.casino_list || []);
    const image_lang = i18n.language === LOCALS.cn ? 'CH' : 'EN';
    const [loginConfirmShow, setLoginConfirmShow] = useState(false);
    const tCommon = t("common", { returnObjects: true });
    const [openLoginModal, setOpenLoginModal] = useState(false);

    const isLogin = !!user?.ukey;

    const isMobileDevice = useMobileDetect();
    const navigate = useNavigate();
    const location = useLocation();

    // 回到原座位：進站時 seat_cacc 非空 → 自動切群組 tab、開彈窗於該分區/頁
    // （客戶 2026-11-02 需求；每次掛載只做一次，數字碼等未知格式安靜跳過）
    const seatRestoredRef = useRef(false);
    useEffect(() => {
        if (!IS_SEAT_ONLY || seatRestoredRef.current) return;
        if (location.state?.reopenCasinoSeat) return; // 從遊戲返回的重開流程優先
        if (!user?.seat_cacc || !gameGroups.length) return;
        const decoded = decodeSeatCacc(user.seat_cacc);
        if (!decoded) return;
        const game = findSeatGame(user, decoded.groupKey, decoded.gameCode);
        if (!game) return;
        const grp = decoded.groupKey === 'JP' ? CASINO_TAB_GRP : GRP_BY_SEAT_GROUP[decoded.groupKey];
        const idx = gameGroups.findIndex((g) => g.grp === grp);
        if (idx === -1) return;
        seatRestoredRef.current = true;
        setActiveTab(idx);
        updateHomeActiveTab(grp);
        setSelectedCasinoGame({
            game,
            groupKey: decoded.groupKey,
            regionKey: decoded.regionKey,
            page: decoded.page,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, gameGroups, location.state]);

    // Handle reopen casino seat modal when returning from game
    useEffect(() => {
        if (!location.state?.reopenCasinoSeat || !location.state?.casinoGame) return;
        if (IS_SEAT_ONLY && !user) return; // 等 user 載入（刷新中）再處理
        const groupKey = location.state.casinoGroupKey || 'JP'; // 舊 state 形狀 fallback
        const gameCode = location.state.casinoGame[0];
        // 關彈窗後 reGetuserInfo 已刷新清單，不可沿用舊物件；查無（下架）則不重開
        const fresh = IS_SEAT_ONLY
            ? findSeatGame(user, groupKey, gameCode)
            : location.state.casinoGame;
        if (fresh) {
            setSelectedCasinoGame({
                game: fresh,
                groupKey: IS_SEAT_ONLY ? groupKey : null,
                regionKey: location.state.casinoRegionKey || null,
            });
        }
        // Clear the state to prevent reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, navigate, location.pathname, user]);
    // #region ui
    const handleTabClick = (index) => {
        setActiveTab(index);
        updateHomeActiveTab(gameGroups[index]?.grp);
    };
    const handleGameClick = (game) => async (event) => {
        event.preventDefault();

        // if (!game.isFree && !isLogin && !IS_NEW_AW) {
        if (!isLogin && !(IS_NEW_AW || IS_AGT)) {

            setLoginConfirmShow(true)
            return;
        }

        // const newTab = window.open('', '_blank')


        try {
            let url;
            if (game.source === 'BBAG') {
                url = await getBBAGGameUrl(game.gameID);
            } else {
                // url = await getGameUrl(game.gameID, game.isFree || gameGroups?.[activeTab]?.grp === 'F');
                url = await getGameUrl(game.gameID, gameGroups?.[activeTab]?.grp === 'F');

            }

            if (url) {
                const urlObj = new URL(url);

                // Set or replace the 'lang' query parameter
                let langCode = LOCALS_IGS_CODE_MAP[i18n.language];
                if (langCode) {
                    urlObj.searchParams.set('lang', langCode);
                }
                if (game.source === 'BBAG') {
                    urlObj.searchParams.set('game_id', game.gameID);
                }

                // urlObj.searchParams.set('gameID', '5702');

                // Determine if additional parameters should be added based on the device type
                const modifiedUrl = isMobileDevice ? urlObj.toString() : `${urlObj.toString()}&fullscreen=1`;

                navigate(SWITCH_ROUTES.GAME, {
                    state: {
                        gameUrl: modifiedUrl,
                    },
                });
            }

        } catch (error) {
            console.log(' error ->  ', error);
        }

        // url ? (newTab.location.href = url) : newTab.close();
        //ryan
        // if (url) window.location.href = url;
    };
    // #endregion

    // #region call api
    const getNewsData = async () => {
        try {
            const res = await getNews(i18n.language);
            if (!res || !Array.isArray(res)) throw Error('getNewsData error.');
            setNews(res);
        } catch (error) {
            setNews([]);
            console.error(error);
        }
    };

    const getGameVideosData = async () => {
        try {
            const res = await getGameVideos(i18n.language);
            if (!res || !Array.isArray(res)) throw Error('getGameVideosData error.');
            setGameVideos(res);
        } catch (error) {
            setGameVideos([]);
            console.error(error);
        }
    };

    const getEducationalTrainingData = async () => {
        try {
            const res = await getEducationalTraining(i18n.language);
            if (!res || !Array.isArray(res)) throw Error('getEducationalTrainingData error.');
            setEducationalTraining(res);
        } catch (error) {
            setEducationalTraining([]);
            console.error(error);
        }
    };

    const getCommissionCalculationData = async () => {
        try {
            const res = await getCommissionCalculation(i18n.language);
            if (!res || !Array.isArray(res)) throw Error('getCommissionCalculationData error.');
            setCommissionCalculation(res);
        } catch (error) {
            setCommissionCalculation([]);
            console.error(error);
        }
    };
    const getGroups = async () => {
        try {
            const res = await getGameGroup(i18n.language);
            if (!res || !Array.isArray(res)) throw Error('getGroups error.');

            let grouplist = res;
            if (IS_SEAT_ONLY) {
                grouplist = res.filter((g) => {
                    if (g.grp === 'F') return false; // 不再提供試玩
                    const seatKey = SEAT_GROUP_BY_GRP[g.grp];
                    if (seatKey) return !!getSeatList(user, seatKey); // D/C/B 有清單才顯示
                    return true; // GUIDE 等內容群組保留
                });
            } else if (user && user.try && user.try === 'N') {
                grouplist = res.filter((e) => e.grp !== 'F');
            }

            // Insert CASINO/JackPot tab if user has casino access
            if (hasCasino) {
                const casinoTab = {
                    id: 'casino',
                    grp: CASINO_TAB_GRP,
                    name: IS_SEAT_ONLY ? (tCommon['group@jackpot'] || 'JackPot') : 'CASINO',
                    imagePath: '/images/groups/casino.svg',
                };
                let insertIdx;
                if (IS_SEAT_ONLY) {
                    // 插在第一個遊戲群組（D/C/B）之前；都沒有則排最後
                    const firstGameIdx = grouplist.findIndex((g) => SEAT_GROUP_BY_GRP[g.grp]);
                    insertIdx = firstGameIdx !== -1 ? firstGameIdx : grouplist.length;
                } else {
                    // Find position after FREE GAME (F) or at position 1
                    const freeGameIdx = grouplist.findIndex((g) => g.grp === 'F');
                    insertIdx = freeGameIdx !== -1 ? freeGameIdx + 1 : 1;
                }
                grouplist = [
                    ...grouplist.slice(0, insertIdx),
                    casinoTab,
                    ...grouplist.slice(insertIdx),
                ];
            }

            setGameGroups(grouplist);

            // homeActiveTab 儲存 grp 字串（舊值為數字時 findIndex 得 -1 → 走預設）
            let idx = grouplist.findIndex((g) => g.grp === homeActiveTab);
            if (idx === -1) {
                const defIdx = grouplist.findIndex((g) => g.defaultTab);
                idx = defIdx !== -1 ? defIdx : 0; // 群組消失 fallback 第一個可見 tab
            }
            setActiveTab(idx);
            updateHomeActiveTab(grouplist[idx]?.grp);
        } catch (error) {
            setGameGroups([]);
            console.error(error);
        }
    };

    // const getGames = async (groupId) => {
    //     try {
    //         const res = await getGameListByGroup(groupId);
    //         if (res.rtn !== STATUS_CODE.SUCCESS) throw Error(res.msg);
    //         setGames(res.game_list);
    //     } catch (error) {
    //         setGames([]);
    //         console.error(error);
    //     }
    // };

    const getGamesByListId = async (listId) => {
        try {
            const res = await getGameListByListId(listId);
            setGames(res);
        } catch (error) {
            setGames([]);
            console.error(error);
        }
    };

    const getGameUrl = async (gameId, isFree = false) => {
        try {

            console.log(' user ->  ',user);

            let params = { gid: gameId };
            if (!user || user.mode === 1) {
                params.dvc = user?.device_id || getDVCInLS();
            } else {
                params.ukey = user.ukey;
            }

            if (isFree) {
                params.tp = 'TRY';
            }

            if (!user || !user.ukey) {

                if (user?.device_id) {
                    params.dvc = user?.device_id;
                }

                const res = await getTryGameTempUrl(params);
                if (res.rtn !== STATUS_CODE.SUCCESS) throw Error(res.msg);
    
                return res.play_url;
            } 

            console.log( ' is Free --->  ',isFree)
            if (isFree || IS_AGT) {
                 if (!!tryUKey) {
                    params.ukey = tryUKey;
                }

                const res = await getTryGameUrl(params);
                if (res.rtn !== STATUS_CODE.SUCCESS) throw Error(res.msg);
    
                return res.play_url;
            } else {
                const res = await getPlayGameUrl(params);
                if (res.rtn !== STATUS_CODE.SUCCESS) throw Error(res.msg);
    
                return res.play_url;
            }

  
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const getBBAGGameUrl = async (gameId) => {
        try {
            let params = { gid: gameId };
            if (!user || user.mode === 1) {
                params.dvc = user?.device_id || getDVCInLS();
            } else {
                params.ukey = user.ukey;
            }
            const res = await getBBAGPlayGameUrl(params);
            if (res.rtn !== STATUS_CODE.SUCCESS) throw Error(res.msg);
            console.log(' getBBAGGameUrl -->  ', res)
            return res.url;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    // #endregion

    useEffect(() => {
        getNewsData();
        getGameVideosData();
        getEducationalTrainingData();
        getCommissionCalculationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // 群組清單依 user 重算：四組 *_list 在 user 載入/刷新後才齊，
    // 關彈窗 reGetuserInfo 後群組會重算（涵蓋「群組消失 fallback」）
    useEffect(() => {
        getGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, user]);

    useEffect(() => {
        // setGames(null)

        const grp = gameGroups?.[activeTab]?.grp;
        const listId = gameGroups?.[activeTab]?.listID;
        const isSeatGrp = grp === CASINO_TAB_GRP || (IS_SEAT_ONLY && !!SEAT_GROUP_BY_GRP[grp]);
        if (grp !== NEWS_TAB_GRP && !isSeatGrp) {
            if (listId) {
                getGamesByListId(listId);
            } else {
                setGames([])
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, gameGroups]);

    const categoryItemWidth =
        gameGroups?.length > 0 ? `${(1 / gameGroups.length) * 100}%` : '100%';

    // 入座制群組分支：CASINO/JackPot 固定是；D/C/B 只在 IS_SEAT_ONLY 時走入座
    const activeGrp = gameGroups[activeTab]?.grp;
    const activeSeatGroupKey =
        activeGrp === CASINO_TAB_GRP ? 'JP' : (IS_SEAT_ONLY ? SEAT_GROUP_BY_GRP[activeGrp] : undefined);
    return (
        <Tabs>
            <TabHeader>
                <nav>
                    {gameGroups.map((group, index) => (
                        <Tab
                            key={index}
                            className={classNames({
                                active: activeTab === index,
                            })}
                            style={{ width: categoryItemWidth }}
                            onClick={() => handleTabClick(index)}
                        >
                            <div>
                                {group.grp === CASINO_TAB_GRP ? (
                                    <CasinoIconWrapper>
                                        <CasinoIconSvg />
                                    </CasinoIconWrapper>
                                ) : (
                                    <ReactSVG
                                        src={`${IMAGE_DOMAINS.BO}${group.imagePath}`}
                                    />
                                )}
                                <p>{group.name}</p>
                            </div>
                        </Tab>
                    ))}
                </nav>
            </TabHeader>
            {gameGroups[activeTab]?.grp === NEWS_TAB_GRP ? (
                <NewsPane>
                    {!news ? (
                        <div className="game-loading-wrap">
                            <img
                                src="images/shared/loading.gif"
                                alt="brand-logo"
                            />
                        </div>
                    ) : (
                        news.map((n) => (
                            <NewItem
                                key={n.id}
                                onClick={() => setSelectedNews({ ...n })}
                                item={{ ...n }}
                            />
                        ))
                    )}
                </NewsPane>
            ) : gameGroups[activeTab]?.grp === GAME_VIDEOS_TAB_GRP ? (
                <NewsPane>
                    {!gameVideos ? (
                        <div className="game-loading-wrap">
                            <img
                                src="images/shared/loading.gif"
                                alt="loading"
                            />
                        </div>
                    ) : (
                        gameVideos.map((item) => (
                            <NewItem
                                key={item.id}
                                onClick={() => setSelectedGameVideo({ ...item })}
                                item={{ ...item }}
                            />
                        ))
                    )}
                </NewsPane>
            ) : gameGroups[activeTab]?.grp === EDUCATIONAL_TRAINING_TAB_GRP ? (
                <NewsPane>
                    {!educationalTraining ? (
                        <div className="game-loading-wrap">
                            <img
                                src="images/shared/loading.gif"
                                alt="loading"
                            />
                        </div>
                    ) : (
                        educationalTraining.map((item) => (
                            <NewItem
                                key={item.id}
                                onClick={() => setSelectedEducationalTraining({ ...item })}
                                item={{ ...item }}
                            />
                        ))
                    )}
                </NewsPane>
            ) : gameGroups[activeTab]?.grp === COMMISSION_CALCULATION_TAB_GRP ? (
                <NewsPane>
                    {!commissionCalculation ? (
                        <div className="game-loading-wrap">
                            <img
                                src="images/shared/loading.gif"
                                alt="loading"
                            />
                        </div>
                    ) : (
                        commissionCalculation.map((item) => (
                            <NewItem
                                key={item.id}
                                onClick={() => setSelectedCommissionCalculation({ ...item })}
                                item={{ ...item }}
                            />
                        ))
                    )}
                </NewsPane>
            ) : activeSeatGroupKey ? (
                <TabPane>
                    <ul>
                        {(activeSeatGroupKey === 'JP' ? casinoList : (getSeatList(user, activeSeatGroupKey) || [])).map((g) => (
                            <li key={g[0]}>
                                <CasinoGameCard
                                    game={g}
                                    onClick={(clicked) =>
                                        setSelectedCasinoGame({
                                            game: clicked,
                                            groupKey: IS_SEAT_ONLY ? activeSeatGroupKey : null,
                                            regionKey: null,
                                        })
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                </TabPane>
            ) : (
                <TabPane>
                    {games !== null ? (
                        <>
                            <ul>
                                {games.map((game, itemIndex) => (
                                    <li key={itemIndex}>
                                        <a
                                            href="/#"
                                            // target="main"
                                            rel="noopener noreferrer"
                                            onClick={handleGameClick(game)}

                                        >
                                            <div style={{
                                                position: "relative"
                                            }}>
                                                <img
                                                    src={`${IMAGE_DOMAINS.ORIGIN}/${image_lang === 'CH' ? game.imagePathCH : game.imagePathEN}`}
                                                    alt={image_lang === 'CH' ? game.nameCH : game.nameEN}
                                                    title={image_lang === 'CH' ? game.nameCH : game.nameEN}
                                                // style={{ opacity: (game.isFree || isLogin) ? "1" : "0.5" }}


                                                />

                                                {!game.isFree && !isLogin && gameGroups?.[activeTab]?.grp !== 'F' && (
                                                    <div
                                                        style={{
                                                            width: '90%',
                                                            position: "absolute",
                                                            bottom: 0, // Aligned to the bottom of the image
                                                            left: '5%',
                                                            right: '5%', // Ensures the div spans the entire width of the image
                                                            borderRadius: "0 0 6px 6px",
                                                            height: "40%", // Mask covers the bottom half of the image
                                                            backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent black mask
                                                            display: "flex",
                                                            justifyContent: "flex-end", // Right-aligns the lock icon
                                                            alignItems: "center", // Bottom-aligns the lock icon

                                                        }}
                                                    >
                                                        <div style={{ width: "30%" }}>
                                                            <img alt='lock' src='/images/lock.png' />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>



                                            <p>
                                                {image_lang === 'CH' ? game.nameCH : game.nameEN}
                                            </p>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            {/* {!games.length && <i>目前沒有資料</i>} */}
                        </>
                    ) : (
                        <div className="game-loading-wrap">
                            <img
                                src="images/shared/loading.gif"
                                alt="loading"
                            />
                        </div>
                    )}
                </TabPane>
            )
            }
            <NewsModal
                show={selectedNews !== null}
                onClose={() => setSelectedNews(null)}
                {...selectedNews}
            />
            <NewsModal
                show={selectedGameVideo !== null}
                onClose={() => setSelectedGameVideo(null)}
                {...selectedGameVideo}
            />
            <NewsModal
                show={selectedEducationalTraining !== null}
                onClose={() => setSelectedEducationalTraining(null)}
                {...selectedEducationalTraining}
            />
            <NewsModal
                show={selectedCommissionCalculation !== null}
                onClose={() => setSelectedCommissionCalculation(null)}
                {...selectedCommissionCalculation}
            />
            <ConfirmModal
                show={loginConfirmShow}
                message={tCommon['common@login-to-access-game']}
                actionTitle={tCommon['common@login']}
                onConfirm={() => {
                    setLoginConfirmShow(false);
                    setOpenLoginModal(true)
                }}
                onCancel={() => setLoginConfirmShow(false)}
            />
            {
                openLoginModal && (
                    <LoginModal onToggleLoginModal={() => { setOpenLoginModal((prev) => !prev); }} />
                )
            }
            <CasinoSeatModal
                show={selectedCasinoGame !== null}
                onClose={() => {
                    setSelectedCasinoGame(null);
                    // Refresh user info to update casino list seat counts
                    // （無參數：reGetuserInfo 會自己選 ukey 優先的查詢參數）
                    if (isLogin) {
                        reGetuserInfo();
                    }
                }}
                game={selectedCasinoGame?.game}
                groupKey={selectedCasinoGame?.groupKey}
                initialRegionKey={selectedCasinoGame?.regionKey}
                initialPage={selectedCasinoGame?.page}
            />

        </Tabs >
    );
}
const NewItem = (props) => {
    const {
        item: { title, imagePath },
        onClick,
    } = props;
    // const { id, title, imagePath, sort, content } = item
    return (
        <>
            <div onClick={onClick}>
                <img src={`${IMAGE_DOMAINS.BO}${imagePath}`} alt={title} />
                {/* <img src={`/images/temp/news/${imagePath}`} /> */}
            </div>
        </>
    );
};

// #region css
const Tabs = styled.div`
    .game-loading-wrap {
        padding-top: 50px;
        width: 100%;
        > img {
            height: 40px;
            width: 40px;
        }
    }

    padding-bottom: 20px;
`;

const TabHeader = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    nav {
        height: 5rem;
        text-shadow: none;
        width: 140%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        white-space: nowrap;
        overflow-y: hidden;
        box-sizing: border-box;
        touch-action: pan-x;

        @media (min-width: ${MAX_MOBILE_WIDTH}px) {
            width: 120%;
        }

        @media (min-width: ${MAX_PAD_WIDTH}px) {
            height: 6rem;
            width: 100%;
        }
    }
`;



const Tab = styled.a`
    text-align: center;
    padding: 0 0.5rem;
    flex: 1;
    height: 100%;
    transition: all 0.3s ease-in-out;
    color: ${({ theme }) => theme.primary};
    background: #fff;
    border: ${({ theme }) => `1px solid ${theme.primary}`};
    cursor: pointer;
    box-sizing: border-box;

    &.active {
        color: white;
        background: ${({ theme }) => theme.primary};
        path {
            fill: white;
        }
    }

    & > div {
        display: inline-block;
        max-width: 50px;
        width: 100%;
        height: 100%;

        @media (min-width: ${MAX_PAD_WIDTH}px) {
            max-width: 60px;
            padding-top: 5px;
        }

        & > div {
            display: block;
            line-height: 1;
        }


    }

    svg {
        width: 100%;
        height: 100%;
        path {
            fill: ${({ theme }) => theme.primary};
        }
    }

    p {
        display: flex;
        justify-content: center;
        margin: 0 auto;
        font-size: 0.75rem;
        line-height: 1rem;
    }

    @media (min-width: ${MAX_PAD_WIDTH}px) {
        p {
            font-size: 1rem;
            line-height: 1rem;
        }
    }
`;

const TabPane = styled.div`
    ul {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
        padding: 5px;
        height: 100%;
    }

    li {
        display: flex;
        justify-content: center;
        list-style: none;
        flex: 0 0 auto;
        width: 33.33333333%;

        @media (min-width: ${MAX_PAD_WIDTH}px) {
            width: 16.66666667%;
        }
    }

    a {
        width: 100%;
    }

    img {
        display: block;
        padding: 0.1rem;
        width: 90%;
        margin: 0 auto;
    }
    @media (min-width: ${MAX_PAD_WIDTH}px) {
        img {
            padding: 0px;
        }
    }
    p {
        width: 100%;
        margin: 0 auto;
        font-size: 14px;
        text-align: center;
        word-break: break-word;
    }

    .seat-count {
        display: block;
        width: 100%;
        margin: 0 auto;
        font-size: 12px;
        text-align: center;
        color: #666;
    }

    i {
        display: block;
        padding-bottom: 1rem;
        text-align: center;
        font-style: normal;
        color: #666;
        height: 5rem;
    }
`;

const NewsPane = styled.div`
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
    padding: 5px;
    height: 100%;

    > div {
        display: flex;
        justify-content: center;
        list-style: none;
        padding: 0.5rem;
        flex: 0 0 auto;
        width: 33.33333333%;
        cursor: pointer;
        padding: 0.1rem;

        @media (min-width: ${MAX_PAD_WIDTH}px) {
            width: 20%;
        }

        > img {
            width: 100%;
            height: auto;
        }
    }
`;

/**
 * Casino SVG Icon component
 * Uses clip-path to create transparent cutouts (like other tab icons)
 * so background shows through in selected state
 */
const CasinoIconSvg = () => (
    <svg viewBox="0 0 64 64" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <defs>
            {/* Define the cutout shapes */}
            <clipPath id="slotMachineClip">
                {/* Full area */}
                <rect x="0" y="0" width="64" height="64" />
            </clipPath>
            {/* Mask to cut out the window and buttons */}
            <mask id="slotMachineMask">
                {/* White = visible, Black = hidden */}
                <rect x="0" y="0" width="64" height="64" fill="white" />
                {/* Cut out display window - 3 reel slots */}
                <rect x="14" y="22" width="10" height="20" rx="1" fill="black" />
                <rect x="27" y="22" width="10" height="20" rx="1" fill="black" />
                <rect x="40" y="22" width="10" height="20" rx="1" fill="black" />
                {/* Cut out buttons */}
                <circle cx="20" cy="50" r="3" fill="black" />
                <circle cx="32" cy="50" r="3" fill="black" />
                <circle cx="44" cy="50" r="3" fill="black" />
            </mask>
        </defs>
        {/* Main slot machine shape with mask applied */}
        <g mask="url(#slotMachineMask)">
            {/* Slot machine body */}
            <rect x="8" y="16" width="48" height="40" rx="4" />
            {/* Top section */}
            <rect x="12" y="8" width="40" height="12" rx="2" />
        </g>
        {/* Lever (no cutouts needed) */}
        <rect x="54" y="24" width="6" height="16" rx="2" />
        <circle cx="57" cy="20" r="4" />
    </svg>
);

const CasinoIconWrapper = styled.div`
    width: 100%;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 100%;
        height: 100%;
    }
`;
// #endregion
